import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  Search, 
  TrendingUp, 
  Plus, 
  Loader2, 
  Play,
  Eye,
  ThumbsUp,
  MessageCircle,
  Clock,
  ExternalLink,
  Filter,
  Globe,
  Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import * as api from '@/lib/api';
import { Bot } from 'lucide-react';
import { useLanguage } from '@/lib/LanguageContext';

const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const PodcastCard = ({ podcast, onAdd, adding, addLabel }) => (
  <Card className="clip-card bg-card border-border overflow-hidden">
    <div className="aspect-video relative overflow-hidden">
      <img 
        src={podcast.thumbnail_url || 'https://images.unsplash.com/photo-1660832459346-d069f58410c1?w=320&h=180&fit=crop'} 
        alt={podcast.title}
        className="clip-thumbnail w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
        <Badge className="bg-black/60 text-white border-none">
          <Clock className="w-3 h-3 mr-1" />
          {formatDuration(podcast.duration_seconds)}
        </Badge>
        {podcast.language && (
          <Badge className="bg-blue-500/80 text-white border-none uppercase text-xs">
            {podcast.language}
          </Badge>
        )}
      </div>
      <div className="absolute top-3 right-3">
        <Badge 
          className="bg-primary/90 text-white border-none"
        >
          <TrendingUp className="w-3 h-3 mr-1" />
          {(podcast.trend_score * 100).toFixed(0)}%
        </Badge>
      </div>
    </div>
    <CardContent className="p-4">
      <h3 className="font-medium text-sm line-clamp-2 mb-2" title={podcast.title}>
        {podcast.title}
      </h3>
      <p className="text-xs text-muted-foreground mb-2">{podcast.channel_name}</p>
      
      {podcast.themes && podcast.themes.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {podcast.themes.slice(0, 3).map((theme) => (
            <Badge key={theme} variant="outline" className="text-xs">
              {theme}
            </Badge>
          ))}
        </div>
      )}
      
      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
        <span className="flex items-center gap-1">
          <Eye className="w-3 h-3" />
          {formatNumber(podcast.view_count)}
        </span>
        <span className="flex items-center gap-1">
          <ThumbsUp className="w-3 h-3" />
          {formatNumber(podcast.like_count)}
        </span>
        <span className="flex items-center gap-1">
          <MessageCircle className="w-3 h-3" />
          {formatNumber(podcast.comment_count)}
        </span>
      </div>
      
      <Button 
        className="w-full"
        onClick={() => onAdd(podcast.youtube_id)}
        disabled={adding}
        data-testid={`add-podcast-${podcast.youtube_id}`}
      >
        {adding ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Plus className="w-4 h-4 mr-2" />
        )}
        {addLabel}
      </Button>
    </CardContent>
  </Card>
);

export default function Discovery() {
  const { t, language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [trendingPodcasts, setTrendingPodcasts] = useState([]);
  const [myPodcasts, setMyPodcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [myPodcastsLoading, setMyPodcastsLoading] = useState(true);
  const [addingId, setAddingId] = useState(null);
  
  // Filters
  const [themes, setThemes] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');

  const [pendingAgentTasks, setPendingAgentTasks] = useState(0);

  useEffect(() => {
    fetchTrending();
    fetchFilters();
  }, []);

  // While the Desktop Agent is searching, poll for new results
  useEffect(() => {
    if (!pendingAgentTasks) return undefined;
    const timer = setInterval(fetchTrending, 15000);
    return () => clearInterval(timer);
  }, [pendingAgentTasks]);


  const fetchFilters = async () => {
    try {
      const [themesRes, languagesRes] = await Promise.all([
        api.getPodcastThemes(),
        api.getPodcastLanguages()
      ]);
      setThemes(themesRes.data.themes || []);
      setLanguages(languagesRes.data.languages || []);
    } catch (error) {
      logger.error('Failed to load filters:', error);
    }
  };

  const fetchTrending = async () => {
    setTrendingLoading(true);
    try {
      const data = await api.getDiscoveryResults(null, 24);
      setTrendingPodcasts(data.results);
      setPendingAgentTasks(data.pending_tasks || 0);
    } catch (error) {
      toast.error('Falha ao carregar descobertas do Agente');
    } finally {
      setTrendingLoading(false);
    }
  };

  const fetchMyPodcasts = useCallback(async () => {
    setMyPodcastsLoading(true);
    try {
      const params = { limit: 50 };
      if (selectedTheme !== 'all') params.theme = selectedTheme;
      if (selectedLanguage !== 'all') params.language = selectedLanguage;
      
      const response = await api.getPodcasts(params);
      setMyPodcasts(response.data.podcasts || []);
    } catch (error) {
      logger.error('Failed to load my podcasts:', error);
    } finally {
      setMyPodcastsLoading(false);
    }
  }, [selectedTheme, selectedLanguage]);

  useEffect(() => {
    fetchMyPodcasts();
  }, [fetchMyPodcasts]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const data = await api.discoverySearch(searchQuery, 12);
      setSearchResults(data.results);
      if (data.task_id) {
        setPendingAgentTasks((n) => n + 1);
        toast.info('Busca enviada ao Agente Desktop. Os vídeos aparecerão aqui assim que ele terminar.');
      } else if (data.results.length) {
        toast.success(`${t('discovery.found')} ${data.results.length} ${t('discovery.podcasts')}`);
      } else {
        toast.info('O Agente já está buscando esse termo. Aguarde os resultados.');
      }
    } catch (error) {
      toast.error('Falha ao enviar busca ao Agente');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPodcast = async (youtubeId) => {
    setAddingId(youtubeId);
    try {
      await api.addPodcastForProcessing(youtubeId);
      toast.success(t('discovery.podcastAdded'));
      fetchMyPodcasts();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('discovery.alreadyAdded'));
      } else {
        toast.error('Failed to add podcast');
      }
    } finally {
      setAddingId(null);
    }
  };

  const getThemeName = (themeId) => {
    const theme = themes.find(t => t.id === themeId);
    if (!theme) return themeId;
    return language === 'pt-BR' ? theme.name : theme.name_en;
  };

  const getLanguageName = (code) => {
    const lang = languages.find(l => l.code === code);
    if (!lang) return code;
    return language === 'pt-BR' ? lang.name_pt : lang.name;
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="discovery-page">
      {/* Search Header */}
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">{t('discovery.title')}</h1>
          <p className="text-muted-foreground">{t('discovery.subtitle')}</p>
        </div>
        
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('discovery.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 bg-muted/50 border-border focus:border-primary"
              data-testid="search-input"
            />
          </div>
          <Button 
            type="submit" 
            className="h-12 px-6 glow-primary"
            disabled={loading}
            data-testid="search-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Search className="w-5 h-5 mr-2" />
                {t('discovery.search')}
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Filter className="w-5 h-5" />
              <span className="font-medium">{t('discovery.filters') || 'Filtros'}:</span>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-muted-foreground" />
                <Select value={selectedTheme} onValueChange={setSelectedTheme}>
                  <SelectTrigger className="w-44 bg-muted/50" data-testid="theme-filter">
                    <SelectValue placeholder={t('discovery.selectTheme') || 'Tema'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('discovery.allThemes') || 'Todos os Temas'}</SelectItem>
                    {themes.map((theme) => (
                      <SelectItem key={theme.id} value={theme.id}>
                        {language === 'pt-BR' ? theme.name : theme.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger className="w-40 bg-muted/50" data-testid="language-filter">
                    <SelectValue placeholder={t('discovery.selectLanguage') || 'Idioma'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('discovery.allLanguages') || 'Todos os Idiomas'}</SelectItem>
                    {languages.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {language === 'pt-BR' ? lang.name_pt : lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Tabs */}
      <Tabs defaultValue="my-podcasts" className="w-full">
        <TabsList className="bg-muted/50 border border-border">
          <TabsTrigger value="my-podcasts" data-testid="my-podcasts-tab">
            <Play className="w-4 h-4 mr-2" />
            {t('discovery.myPodcasts') || 'Meus Podcasts'} ({myPodcasts.length})
          </TabsTrigger>
          <TabsTrigger value="trending" data-testid="trending-tab">
            <TrendingUp className="w-4 h-4 mr-2" />
            {t('discovery.trending')}
          </TabsTrigger>
          <TabsTrigger value="search" data-testid="search-results-tab">
            <Search className="w-4 h-4 mr-2" />
            {t('discovery.searchResults')} ({searchResults.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-podcasts" className="mt-6">
          {myPodcastsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array(8).fill(0).map((_, i) => (
                <Card key={`skeleton-podcast-${i}`} className="bg-card border-border overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <CardContent className="p-4 space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-10 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : myPodcasts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {myPodcasts.map((podcast, index) => (
                <PodcastCard
                  key={podcast.id || index}
                  podcast={podcast}
                  onAdd={handleAddPodcast}
                  adding={addingId === podcast.youtube_id}
                  addLabel={t('discovery.analyze') || 'Analisar'}
                />
              ))}
            </div>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="py-16 text-center">
                <Play className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-2">
                  {t('discovery.noPodcasts') || 'Nenhum podcast adicionado ainda'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('discovery.addPodcastHint') || 'Adicione podcasts da aba Trending ou por URL'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="trending" className="mt-6 space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground rounded-lg border border-border bg-muted/30 px-4 py-2" data-testid="discovery-agent-status">
            <Bot className="w-4 h-4 text-primary" />
            {pendingAgentTasks > 0
              ? `${pendingAgentTasks} busca(s) em andamento no Agente Desktop — atualizando automaticamente`
              : trendingPodcasts.length
                ? 'Vídeos descobertos manualmente pelo Agente Desktop (sem API do YouTube)'
                : 'Nenhuma descoberta ainda. Pesquise um tema acima para o Agente buscar no YouTube.'}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {trendingLoading ? (
              Array(8).fill(0).map((_, i) => (
                <Card key={`skeleton-trending-${i}`} className="bg-card border-border overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <CardContent className="p-4 space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-10 w-full" />
                  </CardContent>
                </Card>
              ))
            ) : (
              trendingPodcasts.map((podcast, index) => (
                <PodcastCard
                  key={podcast.youtube_id || index}
                  podcast={podcast}
                  onAdd={handleAddPodcast}
                  adding={addingId === podcast.youtube_id}
                  addLabel={t('discovery.addForProcessing')}
                />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="search" className="mt-6">
          {searchResults.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {searchResults.map((podcast, index) => (
                <PodcastCard
                  key={podcast.youtube_id || index}
                  podcast={podcast}
                  onAdd={handleAddPodcast}
                  adding={addingId === podcast.youtube_id}
                  addLabel={t('discovery.addForProcessing')}
                />
              ))}
            </div>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="py-16 text-center">
                <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-2">
                  {t('discovery.noSearchResults') || 'Nenhum resultado de busca'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('discovery.searchHint') || 'Busque por podcasts para encontrar oportunidades de conteúdo viral'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Quick Add by URL */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <ExternalLink className="w-5 h-5" />
            {t('discovery.addByUrl')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              const url = e.target.url.value;
              const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
              if (match) {
                handleAddPodcast(match[1]);
                e.target.reset();
              } else {
                toast.error(t('discovery.invalidUrl'));
              }
            }}
            className="flex gap-3"
          >
            <Input
              name="url"
              placeholder={t('discovery.pasteUrl')}
              className="flex-1 h-12 bg-muted/50 border-border"
              data-testid="url-input"
            />
            <Button type="submit" className="h-12 px-6" data-testid="add-url-btn">
              <Plus className="w-5 h-5 mr-2" />
              {t('discovery.addVideo')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
